import { useState, useRef, Fragment } from "react"
import { useRouter } from "next/router"
import { ConseilsCard } from "./ConseilsCard"
import { normalizeNodes } from "./normalizer"
import {
	Pagination,
	Container,
	Button,
	SelectMultiple as Select,
	LoadingOverlay,
	EmptyBlock,
	LoadingSpinner,
	Icon,
} from "@/ui"
import { useForm, Controller } from "react-hook-form"
import { useI18n, useUpdateEffect, useNode } from "@/hooks"
import { generateTermsSlugFromIds, generateIdsFromTermsSlug } from "@/lib/utils"
import { useCollectionContext, useCollectionFetcher } from "@/hooks"
import { generateTermsObjectsFromIds } from "./utils"

export const config = {
	id: "mre_templates:conseils_list",
	lazy: true,
}

const ConseilsListWidget = ({ data }) => {
	const scrollRefPagination = useRef()
	const { t } = useI18n()
	const router = useRouter()
	const locale = router.locale
	const { systemRoute, path_18n } = useNode()
	const current_page_alias = path_18n[locale]
	const defaulPagetLimit = 9
	const context = useCollectionContext(data)
	const [pager, setPager] = useState(context.pager)
	const [filters, setFilters] = useState(context.filters)

	// Extract term slug from node alias and convert it to term id.
	const defaultTheme = generateIdsFromTermsSlug(
		systemRoute?._query?.theme?.replaceAll(" ", "+"),
		context.terms.mre_conseils_theme,
		"all"
	)

	const [selectedTheme, setSelectedTheme] = useState(defaultTheme)

	const nodeAliasPath = `${current_page_alias}/{theme}?page={page}`

	// Shallow URL is used to update history URL.
	const [shallowUrl, setShallowUrl] = useState(
		nodeAliasPath
			.replace(
				"{theme}",
				selectedTheme !== null && selectedTheme !== undefined
					? generateTermsSlugFromIds(
							selectedTheme,
							context.terms.mre_conseils_theme,
							"all"
					  )
					: "all"
			)
			.replace("{page}", pager)
	)

	// Fill filter form with default values.
	const { handleSubmit, reset, control } = useForm({
		defaultValues: {
			theme: generateTermsObjectsFromIds(
				defaultTheme,
				context.terms.mre_conseils_theme,
				null
			),
		},
	})

	// Fetch data based on params filters.
	const collection = useCollectionFetcher({
		nodeType: "conseils",
		initialPosts: context.nodes,
		initialPostsCount: context.count,
		params: filters,
	})

	// Format nodes.
	const posts = normalizeNodes(collection.posts)

	// Submit filter form.
	const submitFilterForm = () => {
		// setSelectedTheme(data?.theme)
		setSelectedTheme(selectedTheme)
		setPager(1)
		fetchData(false, 1)
	}

	/**
	 * Reset filter form.
	 */
	const resetFilterForm = () => {
		reset({
			theme: "all",
		})
		setPager(1)
		setSelectedTheme(["all"])
		fetchData(true)
	}

	const fetchData = async (reset = false, page) => {
		const currentPage = page || pager

		// Update pretty path URL.
		setShallowUrl(
			nodeAliasPath
				.replace(
					"{theme}",
					!reset
						? selectedTheme !== null && selectedTheme !== undefined
							? generateTermsSlugFromIds(
									selectedTheme,
									context.terms.mre_conseils_theme,
									"all"
							  )
							: "all"
						: "all"
				)
				.replace("{page}", currentPage)
		)

		setFilters((prev) => {
			let filters = {
				...prev,
			}

			if (
				reset ||
				!selectedTheme ||
				selectedTheme === "all" ||
				selectedTheme[0] === "all" ||
				selectedTheme.length == 0
			) {
				// try to delete previously set theme filters.
				delete filters?.filter?.theme
			} else {
				// Add a theme filter.
				filters.filter.theme = {
					condition: {
						path: "field_vactory_taxonomy_1.drupal_internal__tid",
						operator: "IN",
						value: selectedTheme,
					},
				}
			}

			// Update pager.
			filters.page = {
				...filters.page,
				offset: (currentPage - 1) * (filters?.page?.limit || defaulPagetLimit),
			}

			return filters
		})
	}

	useUpdateEffect(() => {
		if (scrollRefPagination?.current) {
			scrollRefPagination.current.scrollIntoView({
				behavior: "smooth",
			})
		}
		fetchData()
	}, [pager])

	useUpdateEffect(() => {
		if (context.pager !== pager) setPager(context.pager)
	}, [context.pager])

	// Update page url using shallow.
	useUpdateEffect(() => {
		router.push(shallowUrl, undefined, { shallow: true })
	}, [shallowUrl])

	return (
		<div className="relative overflow-hidden" ref={scrollRefPagination}>
			<Icon
				id="awb-symbol-outline"
				className="absolute -top-[300px] -right-[240px] opacity-50  md:-right-[570px] text-primary fill-current w-[400px] md:w-[950px] h-[600px]"
			/>
			<form onSubmit={handleSubmit(submitFilterForm)}>
				<div className="container ">
					<div className="flex relative flex-col space-y-4 md:flex-row md:space-y-0 md:space-x-4 pt-16 pb-10 md:px-9">
						<div>
							<Controller
								name="theme"
								control={control}
								render={({ field }) => (
									<Select
										{...field}
										options={[...(context.terms?.mre_conseils_theme || [])]}
										selected={selectedTheme}
										placeholder={t("Nx: Toutes les thématiques")}
										setSelected={setSelectedTheme}
										className="relative md:w-fit lg:w-72 w-full"
										id="conseils-theme"
									/>
								)}
							/>
						</div>

						<div className="flex flex-col items-center gap-4 md:flex-row w-full">
							<Button
								id="conseils-submit"
								type="submit"
								variant="mre"
								className="w-full md:w-auto h-full"
							>
								{t("Nx: Appliquer")}
							</Button>
							<Button
								id="conseils-reset"
								type="button"
								onClick={resetFilterForm}
								variant="secondary"
								className="w-full md:w-auto"
							>
								{t("Nx: Réinitialiser")}
							</Button>
							{collection.isLoading && (
								<div className="absolute left-1/2 -translate-x-1/2 md:static bottom-2">
									<LoadingSpinner />
								</div>
							)}
						</div>
					</div>
				</div>
			</form>
			<div
				className={`relative pt-4 ${
					parseInt(collection.count) > parseInt(filters?.page?.limit || defaulPagetLimit)
						? "pb-4"
						: "pb-14"
				}`}
			>
				<div className="container ">
					<LoadingOverlay
						background="rgba(0,0,0,0.45)"
						active={collection.isLoading}
						spinner={true}
					>
						{posts?.length > 0 ? (
							<div className="grid gap-5 mx-auto md:grid-cols-2 xl:grid-cols-3 grid-cols-1 md:px-9 w-full">
								{posts.map((post) => (
									<Fragment key={post.id}>
										<ConseilsCard {...post} />
									</Fragment>
								))}
							</div>
						) : (
							<EmptyBlock />
						)}
					</LoadingOverlay>
				</div>
			</div>
			{parseInt(collection.count) >
				parseInt(filters?.page?.limit || defaulPagetLimit) && (
				<Container className="px-4 pb-4 sm:px-6 lg:pb-8 lg:px-8">
					<Pagination
						baseUrl={`${shallowUrl.replace(/page=\d+/, "page={page}")}`}
						contentRef={scrollRefPagination}
						pageSize={filters?.page?.limit || defaulPagetLimit}
						current={pager}
						total={collection.count}
						isLoading={!collection.isLoading}
						// onChange={(page) => setPager(page)}
					/>
				</Container>
			)}
		</div>
	)
}
export default ConseilsListWidget
